import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WinBanner — sits at the top of the Call Detail page.
 *
 * When the call is already Won: shows a green celebratory strip with
 * source attribution (AI vs manual, actor email).
 *
 * When the call isn't Won: shows a light action bar with a "Mark as Won"
 * button so anyone with access to the call (self-served for the agent,
 * overrider for managers) can flip it.
 */
const WinBanner = ({ call, onWon }) => {
  const [busy, setBusy] = useState(false);
  if (!call) return null;

  if (call.won) {
    const src = call.chase_outcome_source;
    const label = src === 'auto' ? 'AI detected from the transcript'
      : src === 'manager' ? `Marked by manager${call.chase_outcome_by ? ` · ${call.chase_outcome_by}` : ''}`
      : src === 'user' ? `Marked by agent${call.chase_outcome_by ? ` · ${call.chase_outcome_by}` : ''}`
      : `Won${call.chase_outcome_by ? ` · ${call.chase_outcome_by}` : ''}`;
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 flex items-center gap-3"
        data-testid="win-banner-won"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-600 text-white">
          <Trophy className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-emerald-900 text-sm">Deal won — great work</p>
          <p className="text-xs text-emerald-700 mt-0.5">{label}</p>
        </div>
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
      </div>
    );
  }

  const markWon = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/calls/${call.call_id}/mark-won`);
      toast.success('Marked as won 🏆');
      onWon?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as won');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl px-5 py-3 flex items-center justify-between gap-3"
      data-testid="win-banner-mark"
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-500">
          <Trophy className="w-5 h-5" />
        </div>
        <div>
          <p className="font-semibold text-slate-800 text-sm">Did this call end in a win?</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Mark it as Won so it counts toward closed-deal stats and the follow-up loop stops chasing it.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={markWon}
        disabled={busy}
        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-50"
        data-testid="mark-as-won-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />}
        Mark as won
      </button>
    </div>
  );
};

export default WinBanner;
